import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTokens } from '../contexts/TokenContext';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import api from '../lib/api';

const MEMBERSHIP = {
  code: 'membership_annual',
  label: 'Annual Membership',
  price: '£29',
  period: '/year',
  credits: 0,
  features: ['Your first screenplay is on us', 'Full platform access', 'Purchase credit packs anytime'],
};

const PACKS = [
  { code: 'writer_pack', label: 'Writer Pack', price: '£15', credits: 15, perCredit: '£1.00' },
  { code: 'production_pack', label: 'Production Pack', price: '£39', credits: 40, perCredit: '£0.98', popular: true },
  { code: 'studio_pack', label: 'Studio Pack', price: '£79', credits: 90, perCredit: '£0.88', best: true },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance, refreshBalance } = useTokens();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [polling, setPolling] = useState(false);
  const [fulfilledPack, setFulfilledPack] = useState(null);
  const [membership, setMembership] = useState({ active: false, until: null });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get('/account/settings').then(({ data }) => {
      if (cancelled) return;
      setMembership({
        active: !!data.membership_active,
        until: data.membership_until || null,
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user, fulfilledPack]);

  // Handle return from Stripe success
  const sessionId = searchParams.get('session_id');

  const pollStatus = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 15) { setPolling(false); return; }
    try {
      const { data } = await api.get(`/billing/session-status?session_id=${sid}`);
      if (data.status === 'fulfilled') {
        setPolling(false);
        setFulfilledPack(data.pack_code);
        refreshBalance();
        // Check for pending action
        const pending = sessionStorage.getItem('sp_pending_action');
        if (pending) {
          sessionStorage.removeItem('sp_pending_action');
          const action = JSON.parse(pending);
          if (action.projectId) {
            setTimeout(() => navigate(`/project/${action.projectId}?resume=${action.type}`), 1500);
            return;
          }
        }
        return;
      }
      if (data.status === 'expired') { setPolling(false); return; }
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch (e) {
      setTimeout(() => pollStatus(sid, attempts + 1), 3000);
    }
  }, [refreshBalance, navigate]);

  useEffect(() => {
    if (sessionId && !fulfilledPack) {
      setPolling(true);
      pollStatus(sessionId);
    }
  }, [sessionId, fulfilledPack, pollStatus]);

  const handleCheckout = async (packCode) => {
    if (!user) { navigate('/login'); return; }
    setLoading(packCode);
    try {
      const { data } = await api.post('/billing/create-checkout-session', {
        pack_code: packCode,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      console.error(e);
      setLoading(null);
    }
  };

  // Success state
  if (fulfilledPack) {
    const isMembership = fulfilledPack === MEMBERSHIP.code;
    const pack = PACKS.find(p => p.code === fulfilledPack) || MEMBERSHIP;
    return (
      <div className="min-h-screen flex items-center justify-center text-[#F5EDE4]" style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(180deg, #0B0F1A 0%, #0E1320 100%)' }}>
        <div className="text-center space-y-6" data-testid="purchase-success">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(214,168,92,0.15)' }}>
            <Check className="h-8 w-8 text-[#D6A85C]" />
          </div>
          <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isMembership ? 'Membership activated' : 'Credits added'}
          </h1>
          <p className="text-sm text-[#F5EDE4]/50">
            {isMembership
              ? 'Your first screenplay is on us. Full platform access unlocked.'
              : `${pack.credits} credits from ${pack.label} are now in your account.`}
          </p>
          <button onClick={() => navigate('/dashboard')} className="px-8 py-3 text-sm font-medium transition-all duration-300" style={{ backgroundColor: '#D6A85C', color: '#0B0F1A' }} data-testid="back-to-writing">
            {isMembership ? 'Start writing your film' : 'Keep writing your screenplay'}
          </button>
        </div>
      </div>
    );
  }

  // Polling state
  if (polling) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#F5EDE4]" style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(180deg, #0B0F1A 0%, #0E1320 100%)' }}>
        <div className="text-center space-y-4">
          <Loader2 className="h-6 w-6 animate-spin text-[#D6A85C] mx-auto" />
          <p className="text-sm text-[#F5EDE4]/50">Confirming your purchase...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[#F5EDE4] relative" style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(180deg, #0B0F1A 0%, #0E1320 100%)' }}>
      {/* Film grain */}
      <div aria-hidden="true" className="fixed inset-0 z-50 pointer-events-none opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '150px' }} />

      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-[#F5EDE4]/[0.06]" style={{ backgroundColor: 'rgba(11,15,26,0.7)' }}>
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(user ? '/dashboard' : '/')} className="flex items-center gap-2 text-[#F5EDE4]/50 hover:text-[#D6A85C] transition-colors duration-300 text-sm" data-testid="pricing-back">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{user ? 'Your Films' : 'Home'}</span>
          </button>
          {balance !== null && (
            <span className="text-xs text-[#F5EDE4]/35" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="pricing-balance">
              {balance} credits
            </span>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-20 relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Keep writing your screenplay
          </h1>
          <p className="text-sm text-[#F5EDE4]/40 max-w-md mx-auto">
            Credits power your screenplay generation and rewrites. Choose a pack that fits your writing pace.
          </p>
        </div>

        {/* Membership */}
        <div className="mb-16">
          <div className="max-w-md mx-auto p-8 border border-[#D6A85C]/20 relative" style={{ backgroundColor: 'rgba(214,168,92,0.04)' }}>
            <div className="absolute -top-3 left-8">
              <span className="px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-[#0B0F1A]" style={{ backgroundColor: '#D6A85C', fontFamily: "'JetBrains Mono', monospace" }}>
                {membership.active ? 'Active Membership' : 'Membership'}
              </span>
            </div>
            {membership.active ? (
              <>
                <p className="text-sm text-[#F5EDE4]/80 mb-2">You're a member.</p>
                {membership.until && (
                  <p className="text-xs text-[#F5EDE4]/40 mb-6" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Renews {new Date(membership.until).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </p>
                )}
                <ul className="space-y-2 mb-2">
                  {MEMBERSHIP.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-[#F5EDE4]/40">
                      <Check className="h-3 w-3 text-[#D6A85C]/60 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>{MEMBERSHIP.price}</span>
                  <span className="text-sm text-[#F5EDE4]/40">{MEMBERSHIP.period}</span>
                </div>
                <p className="text-sm text-[#F5EDE4]/50 mb-6">Your first screenplay included</p>
                <ul className="space-y-2 mb-8">
                  {MEMBERSHIP.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-[#F5EDE4]/40">
                      <Check className="h-3 w-3 text-[#D6A85C]/60 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleCheckout(MEMBERSHIP.code)} disabled={loading === MEMBERSHIP.code}
                  className="w-full py-3 text-sm font-medium border border-[#D6A85C]/30 text-[#D6A85C] hover:bg-[#D6A85C]/10 disabled:opacity-50 transition-all duration-300"
                  data-testid="buy-membership"
                >
                  {loading === MEMBERSHIP.code ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Join Membership'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Credit Packs */}
        <div className="mb-8">
          <h2 className="text-center text-xs font-medium text-[#F5EDE4]/30 uppercase tracking-[0.2em] mb-8" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            Credit Packs
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {PACKS.map(pack => (
            <div key={pack.code} className={`p-6 border relative transition-all duration-300 hover:border-[#D6A85C]/20 ${pack.popular ? 'border-[#D6A85C]/30' : 'border-[#F5EDE4]/[0.06]'}`} style={{ backgroundColor: pack.popular ? 'rgba(214,168,92,0.04)' : 'rgba(245,237,228,0.02)' }}>
              {pack.popular && (
                <div className="absolute -top-3 left-6">
                  <span className="px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-[#0B0F1A]" style={{ backgroundColor: '#D6A85C', fontFamily: "'JetBrains Mono', monospace" }}>
                    Most Popular
                  </span>
                </div>
              )}
              {pack.best && (
                <div className="absolute -top-3 left-6">
                  <span className="px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-[#F5EDE4]/70 border border-[#F5EDE4]/10" style={{ fontFamily: "'JetBrains Mono', monospace", backgroundColor: 'rgba(11,15,26,0.9)' }}>
                    Best Value
                  </span>
                </div>
              )}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-[#F5EDE4]/80 mb-1">{pack.label}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>{pack.price}</span>
                </div>
                <p className="text-xs text-[#F5EDE4]/30 mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {pack.credits} credits &middot; {pack.perCredit}/credit
                </p>
              </div>
              <button onClick={() => handleCheckout(pack.code)} disabled={loading === pack.code}
                className={`w-full py-3 text-sm font-medium disabled:opacity-50 transition-all duration-300 ${pack.popular ? 'text-[#0B0F1A]' : 'border border-[#F5EDE4]/[0.08] text-[#F5EDE4]/60 hover:text-[#D6A85C] hover:border-[#D6A85C]/30'}`}
                style={pack.popular ? { backgroundColor: '#D6A85C' } : {}}
                data-testid={`buy-${pack.code}`}
              >
                {loading === pack.code ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Keep writing your screenplay'}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-[#F5EDE4]/20 mt-12 max-w-sm mx-auto">
          Secure payments via Stripe. Credits never expire.
        </p>
      </main>
    </div>
  );
}
